import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Pencil, Trash2, Star, Plus, ChevronDown, ChevronUp,
  AlertTriangle, Archive, GraduationCap, KeyRound,
  Award, BadgeCheck, Bell, Briefcase, Building2, Globe, Send, ClipboardCheck,
} from "lucide-react";
import { brokersApi, isArchiveStatus } from "@/api/brokers";
import { isSessionExpired, retryUnlessSessionExpired } from "@/api/polling";
import { getBrokerDisplayName } from "@/lib/brokerDisplay";
import { useAssignableUsers } from "@/hooks/useAssignableUsers";
import type { BrokerContactDto, BrokerDetailDto, BrokerStatus } from "@/api/brokers";
import { brokerKeys } from "@/hooks/brokers/brokerKeys";
import { StatusChangeModal } from "@/components/broker-crm/StatusChangeModal";
import { BrokerStageStepper } from "@/components/broker-crm/BrokerStageStepper";
import { StageAdvancePanel } from "@/components/broker-crm/StageAdvancePanel";
import { QualificationsProfileTab } from "@/components/broker-crm/QualificationsProfileTab";
import { BrokerCertificationsTab } from "@/components/broker-crm/BrokerCertificationsTab";
import { AssignToTeamModal } from "@/pages/brokers/AssignToTeamModal";
import { SendApplicationLinkModal } from "@/pages/leads/SendApplicationLinkModal";
import type { LeadConversionTarget } from "@/api/leads";
import { AssigneePicker } from "@/components/broker-crm/AssigneePicker";
import { BrokerDealsTab } from "@/components/broker-crm/BrokerDealsTab";
import { Badge, Button, EmptyState, FormField, Input, OverflowMenu } from "@/design-system/components/ui";
import { AccountNotificationsPanel } from "@/components/portalAbsorption/AccountNotificationsPanel";
import { AccountReviewRequestsTab } from "@/components/portalAbsorption/AccountReviewRequestsTab";
import { NeedsAttentionBadge } from "@/components/portalAbsorption/NeedsAttentionBadge";
import { PortalAccountTab } from "@/components/portalAbsorption/PortalAccountTab";
import { emailFieldError } from "@/lib/emailValidator";
import { phoneFieldError } from "@/lib/phoneValidator";
import { Modal } from "@/components/ui/Modal";
import { PageHeader } from "@/components/layout/PageHeader";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { StarButton } from "@/components/ux";
import { DocumentPanel } from "@/pages/documents/components/DocumentPanel";
import { TaskListMini } from "@/pages/tasks/components/TaskListMini";
import { AppointmentListMini } from "@/pages/calendar/components/AppointmentListMini";
import { CommunicationsTabPanel } from "@/pages/communications/components/CommunicationsTabPanel";
import { NotesTabPanel } from "@/pages/notes/components/NotesTabPanel";
import { ActivityTabPanel } from "@/pages/activity/components/ActivityTabPanel";
import { RelatedConversationsPanel } from "@/components/messaging/RelatedConversationsPanel";
import { OverviewActivityPreview } from "@/components/activity/OverviewActivityPreview";
import { BrokerageAffiliationsPanel } from "@/components/brokerages/BrokerageAffiliationsPanel";
import { PrequalificationSiteTab } from "@/components/partners/PrequalificationSiteTab";
import { useOpenTaskCountByRelated } from "@/pages/tasks/hooks/useWorkTasks";
import { useDateFormatter } from "@/utils/datetime";
import { useCommunicationsRecentCount } from "@/pages/communications/hooks/useCommunications";
import { useNotesRecentCount } from "@/pages/notes/hooks/useNotes";
import { useActivityRecentCount } from "@/pages/activity/hooks/useActivityByOwner";
import { useAuth } from "@/hooks/useAuth";
import { useDialogs } from "@/hooks/useDialogs";
import {
  CROSS_CUTTING_TAB_ICONS,
  EntityTabBar,
  type EntityTabItem,
} from "@/components/layout/EntityTabBar";

// Party-surface standard (§2.1, PartyDetailPage reference) — the front block
// is Overview · Portal account · Review Queue · Notifications, then the
// entity-specific tabs (Brokerages / Qualifications / Certifications / Deals)
// keep their relative order, and the cross-cutting block stays last.
type TabKey =
  | "overview"
  | "portal-account"
  | "brokerages"
  | "qualifications"
  | "deals"
  | "certifications"
  | "prequalification"
  | "review-queue"
  | "documents"
  | "tasks"
  | "calendar"
  | "messages"
  | "communications"
  | "notes"
  | "activity"
  | "notifications";

const TABS: readonly TabKey[] = [
  "overview",
  "portal-account",
  "review-queue",
  "notifications",
  "brokerages",
  "qualifications",
  "certifications",
  "deals",
  "prequalification",
  "documents",
  "tasks",
  "calendar",
  "messages",
  "communications",
  "notes",
  "activity",
] as const;

const TAB_ITEMS: ReadonlyArray<EntityTabItem<TabKey>> = [
  { key: "overview", label: "Overview", icon: CROSS_CUTTING_TAB_ICONS.overview },
  // Party-surface WS-4 — the shared portal-account tab replaces the Overview
  // PortalAccountCard mount.
  { key: "portal-account", label: "Portal account", icon: KeyRound },
  { key: "review-queue", label: "Review Queue", icon: ClipboardCheck },
  // Portal absorption (WS-D2) — broadcasts delivered to the broker's linked
  // portal account, with read state.
  { key: "notifications", label: "Notifications", icon: Bell },
  { key: "brokerages", label: "Brokerages", icon: Building2 },
  { key: "qualifications", label: "Qualifications", icon: BadgeCheck },
  { key: "certifications", label: "Certifications", icon: Award },
  { key: "deals", label: "Deals", icon: Briefcase },
  // Which prequalification site this broker uses (their own, or their covering
  // firm's) and who has completed one through it. Read is gated Brokers.Read
  // server-side — the same permission that opens this page — so the tab needs
  // no per-tab gate; the create/manage affordances inside it self-gate.
  { key: "prequalification", label: "Prequalification", icon: Globe },
  { key: "documents", label: "Documents", icon: CROSS_CUTTING_TAB_ICONS.documents },
  { key: "tasks", label: "Tasks", icon: CROSS_CUTTING_TAB_ICONS.tasks },
  { key: "calendar", label: "Calendar", icon: CROSS_CUTTING_TAB_ICONS.calendar },
  { key: "messages", label: "Messages", icon: CROSS_CUTTING_TAB_ICONS.messages },
  { key: "communications", label: "Communications", icon: CROSS_CUTTING_TAB_ICONS.communications },
  { key: "notes", label: "Notes", icon: CROSS_CUTTING_TAB_ICONS.notes },
  { key: "activity", label: "Activity", icon: CROSS_CUTTING_TAB_ICONS.activity },
] as const;

function isTabKey(value: string | null): value is TabKey {
  return value !== null && (TABS as readonly string[]).includes(value);
}

const STATUS_BADGE: Record<BrokerStatus, "gold" | "info" | "finalized" | "draft" | "review" | "archived" | "error"> = {
  Prospect: "info",
  Qualified: "gold",
  Onboarding: "review",
  Certified: "finalized",
  Inactive: "archived",
  Suspended: "review",
  Disqualified: "error",
  Declined: "archived",
};

export function BrokerDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const dialogs = useDialogs();
  const canUpdate = hasPermission("Brokers.Update");
  const canQualify = hasPermission("Brokers.Qualify");
  // FIND-13a — the broker-side invite affordance is gated by the same
  // permission as the lead-side Send Application Link.
  const canInvite = hasPermission("Leads.Convert");
  // The Review Queue tab reuses the staff staging-review surface, so it's
  // gated by the same permission as /collection/staging.
  const canReviewStaging = hasPermission("Collection.Staging.ReviewAndFile");
  const { date } = useDateFormatter();

  const [params, setParams] = useSearchParams();
  const tabParam = params.get("tab");
  const requestedTab: TabKey = isTabKey(tabParam) ? tabParam : "overview";
  // A user without the staging permission who deep-links to ?tab=review-queue
  // falls back to Overview (the tab is also hidden from the bar below).
  const tab: TabKey =
    requestedTab === "review-queue" && !canReviewStaging ? "overview" : requestedTab;

  const setTab = (next: TabKey) => {
    const updated = new URLSearchParams(params);
    if (next === "overview") updated.delete("tab");
    else updated.set("tab", next);
    setParams(updated, { replace: true });
  };

  const [modal, setModal] = useState<
    | null
    | { type: "status"; broker: BrokerDetailDto }
    | { type: "stage"; broker: BrokerDetailDto; target: BrokerStatus }
    | { type: "assign-team"; broker: BrokerDetailDto }
  >(null);
  const canDisqualify = hasPermission("Brokers.Disqualify");
  // FIND-13a — invite modal open state.
  const [inviteOpen, setInviteOpen] = useState(false);

  const broker = useQuery({
    queryKey: brokerKeys.detail(id),
    queryFn: () => brokersApi.get(id),
    enabled: !!id,
    // Broker "Import from files" (WS-E) — auto-poll while the AI import job
    // is in flight so the Qualifications tab fills in live and the in-
    // progress pill resolves without a manual refresh.
    refetchInterval: (query) => {
      // Session expired — stop polling so an unattended tab doesn't 401 forever.
      if (isSessionExpired(query.state.error)) return false;
      const s = query.state.data?.aiImportStatus;
      return s === "Pending" || s === "Running" ? 3000 : false;
    },
    retry: retryUnlessSessionExpired,
  });

  // FIND-13a — three-state invite status drives the header affordance.
  // WS-D2 — no longer gated on canInvite: the endpoint gates Brokers.Read
  // server-side, and collectionAccountId now also feeds the portal-account
  // card, the needs-attention badge, and the Notifications tab for every
  // viewer of the page (the header invite buttons still gate on canInvite).
  const inviteStatus = useQuery({
    queryKey: brokerKeys.inviteStatus(id),
    queryFn: () => brokersApi.inviteStatus(id),
    enabled: !!id,
  });

  const inviteToApply = useMutation({
    mutationFn: (vars: {
      collectionBundleIds: string[];
      sendPortalAccessOnly: boolean;
      target: LeadConversionTarget;
      brokerageId: string | null;
    }) => brokersApi.inviteToApply(id, vars),
    onSuccess: async (result) => {
      void qc.invalidateQueries({ queryKey: brokerKeys.inviteStatus(id) });
      setInviteOpen(false);
      await dialogs.alert({
        title: "Application invite sent",
        tone: "success",
        message: result.message,
      });
    },
    onError: (e: Error) => {
      void dialogs.alert({ title: "Could not send invite", tone: "error", message: e.message });
    },
  });

  // Qualifications/contacts surface beyond this page (the brokers list's
  // Contact / E&O expires / License expires columns + the alerts strip), so
  // detail edits must stale-mark those reads too.
  const invalidate = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: brokerKeys.detail(id) }),
      qc.invalidateQueries({ queryKey: brokerKeys.lists() }),
      qc.invalidateQueries({ queryKey: brokerKeys.alerts() }),
    ]);

  // Default breadcrumb is URL-derived, which renders the raw broker GUID as
  // the trailing crumb. Override with the brokerage's name once loaded.
  useBreadcrumbs(
    broker.data
      ? [
          { label: "Brokers", href: "/brokers" },
          { label: broker.data.companyName || "Broker" },
        ]
      : null,
  );

  // Tab badges (Layout Standard §5). Each query is enabled only when an
  // id is present; the badge hides when the count is 0.
  const openTaskCountQuery = useOpenTaskCountByRelated("Broker", id || undefined);
  const openTaskCount = openTaskCountQuery.data ?? 0;
  const commsRecent = useCommunicationsRecentCount("broker", id, 30);
  const notesRecent = useNotesRecentCount("broker", id, 7);
  const activityRecent = useActivityRecentCount("broker", id, 7);

  if (broker.isLoading || !broker.data) {
    return <PageHeader eyebrow="Brokers" title="Loading…" />;
  }
  const b = broker.data;
  // The broker's linked portal account (PortalUser id), resolved via the
  // invite-status read — feeds the Portal account tab, the review-queue
  // account scope, the needs-attention badge and the Notifications tab.
  const collectionAccountId = inviteStatus.data?.collectionAccountId ?? null;

  const tabBadge = (key: TabKey): number | null => {
    switch (key) {
      case "tasks":          return openTaskCount > 0 ? openTaskCount : null;
      case "communications": return commsRecent.data && commsRecent.data > 0 ? commsRecent.data : null;
      case "notes":          return notesRecent.data && notesRecent.data > 0 ? notesRecent.data : null;
      case "activity":       return activityRecent.data && activityRecent.data > 0 ? activityRecent.data : null;
      default:               return null;
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Broker"
        // The broker IS the person, not the LLC — the primary contact's name
        // is the H1; the company/LLC (and DBA) drop to the subtitle. Mirrors
        // the Lead detail header convention (FIND-20).
        title={getBrokerDisplayName(b)}
        description={[b.companyName, b.dba ? `DBA ${b.dba}` : null]
          .filter(Boolean)
          .join(" • ") || undefined}
        actions={
          <div className="flex items-center gap-space-2">
            {/* WS6 — graduation affordance: Certified + unteamed + past the
                policy window. Opens the existing Assign-to-team modal. */}
            {b.readyToGraduate && canUpdate && (
              <Button size="sm" onClick={() => setModal({ type: "assign-team", broker: b })}>
                <GraduationCap size={14} strokeWidth={1.5} aria-hidden /> Ready to graduate
              </Button>
            )}
            {/* WS5 — pipeline progression lives on the stage stepper below; the
                status exits (archive / reactivate / disqualify) live behind
                "More actions", positioned to the LEFT of the invite affordance. */}
            {(canUpdate || canDisqualify) && (
              <OverflowMenu
                label="More actions"
                data-testid="broker-more-actions"
                items={[
                  {
                    label: "Change Status",
                    onSelect: () => setModal({ type: "status", broker: b }),
                  },
                ]}
              />
            )}
            {/* FIND-13a — three-state invite affordance, gated by Leads.Convert. */}
            {canInvite && inviteStatus.data?.state === "NotInvited" && (
              <Button
                size="sm"
                onClick={() => setInviteOpen(true)}
                disabled={!inviteStatus.data.canInvite}
                title={!inviteStatus.data.canInvite ? "Broker has no email on file" : undefined}
              >
                <Send size={14} strokeWidth={1.5} aria-hidden /> Send invitation link
              </Button>
            )}
            {canInvite && inviteStatus.data?.state === "InviteSent" && (
              <Button size="sm" variant="secondary" onClick={() => setInviteOpen(true)}>
                <Send size={14} strokeWidth={1.5} aria-hidden /> Resend invitation link
              </Button>
            )}
            {/* Create-in-context for a prequalification site moved OFF the
                header and onto the Prequalification tab, where the empty state
                that motivates it lives. Same component, same launch query. */}
            {/* F6 — the "View portal account" header button was removed; the
                Portal account tab is the in-place management surface now. */}
            {/* Edit — to the RIGHT of the invite affordance; mirrors the
                Persons detail "Edit" link. Relocated out of the summary card. */}
            {canUpdate && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => navigate(`/brokers/${b.id}/edit`)}
              >
                <Pencil size={14} strokeWidth={1.5} aria-hidden /> Edit
              </Button>
            )}
          </div>
        }
      />

      {/* WS5 — the pipeline stage stepper replaces the buried status modal as
          the primary progression control. */}
      <div className="mb-space-3">
        <BrokerStageStepper
          status={b.status}
          teamId={b.teamId}
          teamName={b.teamName}
          canQualify={canQualify}
          onStageClick={(target) => setModal({ type: "stage", broker: b, target })}
          archiveBadge={<Badge status={STATUS_BADGE[b.status]}>{b.status}</Badge>}
        />
      </div>

      {/* Broker Status Refactor §9 — status banners */}
      {b.status === "Disqualified" && (
        <div className="bg-status-error text-white-pure p-space-3 rounded-md mb-space-3">
          <div className="flex items-center gap-space-2 font-display uppercase tracking-wide font-semibold">
            <AlertTriangle size={14} strokeWidth={1.5} aria-hidden />
            Disqualified
          </div>
          <div className="text-small mt-space-1">
            {b.disqualificationReason ?? b.archiveReason ?? "(no reason recorded)"}
            {b.archivedDate && <> — {date(b.archivedDate)}</>}
          </div>
          <div className="text-caption mt-space-1 opacity-80">
            Reactivation requires E1 or E2.
          </div>
        </div>
      )}
      {isArchiveStatus(b.status) && b.status !== "Disqualified" && (
        <div className="bg-gray-mid/10 border border-border-light text-gray-dark p-space-3 rounded-md mb-space-3">
          <div className="flex items-center gap-space-2 font-display uppercase tracking-wide font-semibold text-gray-mid">
            <Archive size={14} strokeWidth={1.5} aria-hidden /> {b.status}
          </div>
          <div className="text-small mt-space-1">
            {b.archiveReason ?? "(no reason recorded)"}
            {b.archivedDate && <> — {date(b.archivedDate)}</>}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-space-2 mb-space-3">
        <StarButton
          entityType="Broker"
          entityId={b.id}
          entityTitle={b.companyName}
          inline={false}
        />
        <Badge status={STATUS_BADGE[b.status]}>{b.status}</Badge>
        <Badge status="info">{b.source}</Badge>
        {/* Portal absorption (WS-D2) — pending portal-review work on the
            broker's linked account, next to the header chips. */}
        <NeedsAttentionBadge portalUserId={inviteStatus.data?.collectionAccountId} />
        {/* Lead Conversion Router (WS-3) — the broker's immutable payee posture.
            A junior links to its covering firm ("Junior · {firm}"). */}
        {b.brokerType === "Junior" ? (
          b.affiliatedBrokerageId ? (
            <Link to={`/brokerages/${b.affiliatedBrokerageId}`}>
              <Badge status="gold" data-testid="broker-type-chip">
                Junior · {b.affiliatedBrokerageName ?? "Brokerage"}
              </Badge>
            </Link>
          ) : (
            <Badge status="gold" data-testid="broker-type-chip">Junior</Badge>
          )
        ) : (
          <Badge status="info" data-testid="broker-type-chip">Independent</Badge>
        )}
        {/* Broker self-apply follow-ups — flags a broker who came in via the
            public application form and hasn't been advanced past Submitted. */}
        {b.isNewApplicant && <Badge status="gold">New applicant</Badge>}
        {/* WS6 — graduated brokers carry their team on the header. */}
        {b.teamId && (
          <Badge status="gold" data-testid="broker-team-chip">Team: {b.teamName}</Badge>
        )}
        {b.hasDisciplinaryAction && (
          <Badge status="error" className="uppercase">
            <AlertTriangle size={12} strokeWidth={1.5} aria-hidden /> Disciplinary
          </Badge>
        )}
        {b.tags && b.tags.length > 0 && b.tags.map((t) => (
          <Badge key={t} status="gold">{t}</Badge>
        ))}
        <span className="text-caption text-gray-mid">
          Created {date(b.createdAt)}
          {b.modifiedAt ? ` · Modified ${date(b.modifiedAt)}` : ""}
        </span>
      </div>
      {b.annualRefresherDueDate && (
        <div className="text-caption text-gray-mid mb-space-3">
          Annual refresher due: <span className="font-mono">{date(b.annualRefresherDueDate)}</span>
        </div>
      )}

      {/* Fully-manual broker lifecycle (UAT broker-page bundle): the
          auto-generated "NEW HANDOFF" banner was removed for brokers. Status
          changes no longer produce a handoff message. */}

      {/* Layout Standard §6.1 — sticky tab bar below the entity header.
          Entity-specific tabs (Qualifications / Deals / Certifications)
          keep their existing positions per §3; the cross-cutting block
          is appended after Documents. */}
      <EntityTabBar
        ariaLabel="Broker sections"
        testIdPrefix="broker"
        items={TAB_ITEMS.filter((it) => it.key !== "review-queue" || canReviewStaging).map(
          (it) => ({ ...it, badge: tabBadge(it.key) }),
        )}
        value={tab}
        onChange={setTab}
        className="mb-space-4"
      />

      {tab === "overview" && (
        <OverviewTab
          broker={b}
          canUpdate={canUpdate}
          onAssign={async (userId) => {
            try {
              await brokersApi.assign(b.id, userId);
              void invalidate();
            } catch (e) {
              void dialogs.alert({
                title: "Reassign failed",
                tone: "error",
                message: e instanceof Error ? e.message : "Unknown error",
              });
            }
          }}
        />
      )}
      {/* Party-surface WS-4 — the shared portal-account tab (replaces the
          Overview PortalAccountCard). The broker invite flow is multi-step
          (Send Application Link modal, bundle selection), so the tab omits
          the single-shot `invite` prop and the header keeps the invite
          affordances (FIND-13a). */}
      {tab === "portal-account" &&
        (inviteStatus.isLoading ? (
          <p className="text-small text-gray-mid italic">Loading portal account…</p>
        ) : (
          <PortalAccountTab
            portalUserId={collectionAccountId}
            emptyHint="No portal account is linked to this broker yet — send an invitation from the header."
          />
        ))}
      {/* Brokerage Module v1.0 — covering-firm affiliations for this broker. */}
      {tab === "brokerages" && (
        <BrokerageAffiliationsPanel
          partnerId={b.id}
          partnerKind="Broker"
          partnerName={b.name || b.companyName}
          brokerType={b.brokerType}
        />
      )}
      {tab === "qualifications" && (
        <QualificationsProfileTab broker={b} canUpdate={canUpdate} />
      )}
      {tab === "deals" && <BrokerDealsTab brokerId={b.id} />}
      {tab === "prequalification" && (
        <PrequalificationSiteTab
          owner={{ kind: "broker", id: b.id, name: getBrokerDisplayName(b) }}
        />
      )}
      {tab === "certifications" && (
        <BrokerCertificationsTab
          brokerId={b.id}
          brokerName={b.companyName}
          canQualify={canQualify}
        />
      )}
      {/* C3 — the standard combined Review Queue + Document Requests tab
          (replaces the bespoke BrokerReviewQueueTab). Scoped by the linked
          portal account when one exists (full account roll-up), else by
          package owner — same rows the old owner-scoped tab showed. */}
      {tab === "review-queue" && (
        <AccountReviewRequestsTab
          scope={
            collectionAccountId
              ? { portalUserId: collectionAccountId }
              : { ownerEntityType: "Broker", ownerEntityId: b.id }
          }
          requestComposer={{
            accountType: "Broker",
            // A Junior broker's default catalog is JuniorBroker-tagged; the
            // account is still provisioned as a Broker (inside the modal).
            brokerType: b.brokerType,
            email: b.email ?? b.portalLoginEmail ?? null,
            owner: { ownerEntityType: "Broker", ownerEntityId: b.id },
          }}
          emptyMessage="This broker hasn't submitted anything for review yet."
        />
      )}
      {tab === "documents" && (
        <DocumentPanel ownerType="Broker" ownerId={b.id} rootName={b.companyName} />
      )}
      {tab === "tasks" && (
        <TaskListMini
          relatedEntityType="Broker"
          relatedEntityId={b.id}
          addButtonLabel="+ Add task linked to this Broker"
        />
      )}
      {tab === "calendar" && (
        <AppointmentListMini
          relatedEntityType="Broker"
          relatedEntityId={b.id}
          entityLabel={b.companyName}
        />
      )}
      {tab === "messages" && (
        <RelatedConversationsPanel
          resourceType="broker"
          resourceId={b.id}
          entityLabel={b.companyName}
        />
      )}
      {tab === "communications" && (
        <CommunicationsTabPanel
          ownerType="broker"
          ownerId={b.id}
          ownerLabel={b.companyName}
        />
      )}
      {tab === "notes" && (
        <NotesTabPanel ownerType="broker" ownerId={b.id} />
      )}
      {tab === "activity" && (
        <ActivityTabPanel ownerType="broker" ownerId={b.id} />
      )}
      {/* Portal absorption (WS-D2) — broadcasts delivered to the broker's
          linked portal account. */}
      {tab === "notifications" &&
        (inviteStatus.isLoading ? (
          <p className="text-small text-gray-mid italic">Loading portal account…</p>
        ) : inviteStatus.data?.collectionAccountId ? (
          <AccountNotificationsPanel
            portalUserId={inviteStatus.data.collectionAccountId}
          />
        ) : (
          <EmptyState
            title="No portal account"
            description="This broker has no linked portal account yet — send an invitation from the header, then notifications delivered to it will appear here."
          />
        ))}

      {modal?.type === "status" && (
        <StatusChangeModal
          broker={modal.broker}
          canArchive={canUpdate}
          canDisqualify={canDisqualify}
          onClose={() => setModal(null)}
          onConfirmArchive={async (target, reason) => {
            await brokersApi.archive(modal.broker.id, target, reason);
            void invalidate();
          }}
          onConfirmDisqualify={async (reason) => {
            await brokersApi.disqualify(modal.broker.id, reason);
            void invalidate();
          }}
          onConfirmReactivate={async (target, notes) => {
            await brokersApi.reactivate(modal.broker.id, target, notes);
            void invalidate();
          }}
        />
      )}
      {/* WS5 — stage-click soft checklist + confirm. */}
      {modal?.type === "stage" && (
        <StageAdvancePanel
          broker={modal.broker}
          target={modal.target}
          onClose={() => setModal(null)}
        />
      )}
      {/* WS6 — graduation modal (shared with the kanban affordance). */}
      {modal?.type === "assign-team" && (
        <AssignToTeamModal broker={modal.broker} onClose={() => setModal(null)} />
      )}
      {/* FIND-13a — reuse the lead-side Send Application Link modal. */}
      <SendApplicationLinkModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        leadEmail={inviteStatus.data?.contactEmail ?? null}
        canManageBundles={hasPermission("Collection.ManageTemplates")}
        showTypeRouter
        mode="broker"
        initialTarget={b.brokerType === "Junior" ? "JuniorBroker" : "IndependentBroker"}
        onConfirm={(vars) => inviteToApply.mutate(vars)}
        submitting={inviteToApply.isPending}
      />
    </>
  );
}

function OverviewTab({
  broker,
  canUpdate,
  onAssign,
}: {
  broker: BrokerDetailDto;
  canUpdate: boolean;
  onAssign: (userId: string) => Promise<void>;
}) {
  const { date } = useDateFormatter();
  const [detailsOpen, setDetailsOpen] = useState(false);
  // Resolve assignment GUIDs to display names (Ownership/Assignment §3
  // assignable surface), mirroring the Lead detail page.
  const assignableUsers = useAssignableUsers();
  const resolveUserName = (userId?: string | null): string | null => {
    if (!userId) return null;
    return assignableUsers.data?.find((u) => u.id === userId)?.displayName ?? userId.slice(0, 8);
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-space-4">
      <div className="bg-white-pure border border-border-light rounded-lg p-space-4 md:col-span-2">
        <h3 className="mb-space-2 font-display font-bold text-small uppercase tracking-wider text-gold-shadow">Broker Summary</h3>
        <dl className="grid grid-cols-2 gap-space-2 text-small">
          <dt className="text-gray-mid">Broker Name</dt><dd>{broker.name ?? "—"}</dd>
          <dt className="text-gray-mid">Broker Title</dt><dd>{broker.title ?? "—"}</dd>
          <dt className="text-gray-mid">Broker Email</dt><dd>{broker.email ?? broker.portalLoginEmail ?? "—"}</dd>
          <dt className="text-gray-mid">Broker Phone</dt><dd>{broker.phone ?? "—"}</dd>
        </dl>
        {/* "Show more" toggle on the divider between the summary details and the
            assignment information — replaces the old eye icon button. Expands
            the full broker application details below. */}
        <div className="mt-space-3 border-t border-border-light pt-space-3">
          <Button
            type="button"
            variant="text"
            size="sm"
            aria-expanded={detailsOpen}
            onClick={() => setDetailsOpen((open) => !open)}
          >
            {detailsOpen ? <ChevronUp size={14} aria-hidden /> : <ChevronDown size={14} aria-hidden />}
            {detailsOpen ? "Show less" : "Show more"}
          </Button>
        </div>
        {detailsOpen && <BrokerApplicationDetails broker={broker} resolveUserName={resolveUserName} />}
        {/* Ownership/Assignment §12 — broker liaison reassignment widget. */}
        <div className="mt-space-3 pt-space-3 border-t border-border-light">
          <AssigneePicker
            currentUserId={broker.assignedC1LiaisonUserId ?? null}
            rolesFilter="C1,C0,Admin"
            canEdit={canUpdate}
            label="Assigned liaison"
            onAssign={onAssign}
          />
          {broker.assignedAt && (
            <div className="mt-space-1 text-caption uppercase tracking-wide text-gray-mid">
              Assigned {date(broker.assignedAt)}
              {broker.assignedByUserId ? ` by ${resolveUserName(broker.assignedByUserId)}` : ""}
            </div>
          )}
        </div>
      </div>

      {/* Layout Standard §4.1 — right-rail Recent Activity preview card.
          Replaces the legacy `Recent activity (N)` panel from BrokerDetail
          v3.0; full feed lives on the Activity tab. */}
      <OverviewActivityPreview
        ownerType="broker"
        ownerId={broker.id}
        detailPath={`/brokers/${broker.id}`}
      />

      {/* Party-surface WS-4 — the portal-account card moved off Overview into
          the dedicated Portal account tab. */}

      {/* Portal absorption (WS-D2) — junior payee routing (Lever C). Read-only,
          rendered entirely from the detail DTO. */}
      {broker.brokerType === "Junior" && <PayeeRoutingCard broker={broker} />}

      {/* Normalized BrokerContact rows are the source of truth for the broker's
          people (name/phone/email/title live here, not on the broker). This
          card manages them and designates the primary. */}
      <BrokerContactsCard broker={broker} canUpdate={canUpdate} />

      {broker.notes && (
        <div className="bg-white-pure border border-border-light rounded-lg p-space-4 md:col-span-3">
          <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow mb-space-2">Notes</h3>
          <p className="text-small whitespace-pre-wrap">{broker.notes}</p>
        </div>
      )}
    </div>
  );
}


// Portal absorption (WS-D2) — read-only explainer for the Lever-C junior
// payee redirect, rendered entirely from BrokerDetailDto (brokerType +
// affiliatedBrokerageId/Name resolved server-side by the SAME rule the
// money routing uses). Shown only for a Junior broker.
function PayeeRoutingCard({ broker }: { broker: BrokerDetailDto }) {
  return (
    <div
      className="bg-white-pure border border-border-light rounded-lg p-space-4 md:col-span-2"
      data-testid="broker-payee-routing-card"
    >
      <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow mb-space-2">
        Payee routing
      </h3>
      <dl className="grid grid-cols-[auto,1fr] gap-x-space-3 gap-y-space-1 text-small">
        <dt className="text-gray-mid">Broker type</dt>
        <dd>Junior</dd>
        <dt className="text-gray-mid">Covering brokerage</dt>
        <dd>
          {broker.affiliatedBrokerageId ? (
            <Link
              to={`/brokerages/${broker.affiliatedBrokerageId}`}
              className="text-gold-shadow hover:underline"
            >
              {broker.affiliatedBrokerageName ?? "Brokerage"}
            </Link>
          ) : (
            <span className="text-status-error">
              None — fees can&apos;t route until an active brokerage covers this broker.
            </span>
          )}
        </dd>
      </dl>
      <p className="text-caption text-gray-mid mt-space-2 mb-0">
        Fees for this broker&apos;s deals are paid to the covering brokerage —
        an identity-only redirect (fee sizing is unchanged), and committed
        loans keep the brokerage snapshotted at closing even if the
        affiliation later changes.
      </p>
    </div>
  );
}

function BrokerApplicationDetails({
  broker,
  resolveUserName,
}: {
  broker: BrokerDetailDto;
  resolveUserName: (userId?: string | null) => string | null;
}) {
  const empty = "—";
  const address = [
    broker.addressLine1,
    broker.addressLine2,
    broker.city,
    broker.state,
    broker.postalCode,
  ].filter(Boolean).join(", ");
  const money = broker.eoCoverageAmount ? `$${broker.eoCoverageAmount.toLocaleString()}` : empty;
  const groups = [
    {
      title: "Contact & Company",
      rows: [
        ["Your Company Name", broker.companyName],
        ["First Name & Last Name", broker.name],
        ["Job Title", broker.title],
        ["Primary contact email", broker.email ?? broker.portalLoginEmail],
        ["Primary contact phone", broker.phone],
        ["Business Mailing Address", address],
        ["LinkedIn Profile", broker.linkedInProfile],
      ],
    },
    {
      title: "Experience & Markets",
      rows: [
        ["Years of Experience", broker.yearsOfExperience?.toString()],
        ["Number of Transactions Closed", broker.numberOfTransactionsClosed?.toString()],
        ["Primary Areas of Expertise", broker.primaryAreasOfExpertise],
        ["Industries Served", broker.industriesServed],
        ["States and Geographic Markets Served", broker.geographicMarketsServed],
      ],
    },
    {
      title: "Credentials & Insurance",
      rows: [
        ["Professional Certifications, Licenses, and Designations", broker.professionalCertificationsLicensesDesignations],
        ["E&O Insurance Provider", broker.eoInsuranceProvider],
        ["E&O Insurance Policy Number", broker.eoPolicyNumber],
        ["E&O Insurance Policy Coverage Amount (USD)", money],
        ["E&O Insurance Policy Expiration Date", broker.eoExpirationDate],
        ["Assigned manager (C0)", resolveUserName(broker.assignedC0ManagerUserId)],
      ],
    },
    {
      title: "Biography & Phoenix Interest",
      rows: [
        ["Professional Biography or Resume", "File attachment import is not supported yet."],
        ["Why Are You Interested in Phoenix Capital?", broker.phoenixCapitalInterest],
      ],
    },
  ];

  return (
    <div className="mt-space-3 flex flex-col gap-space-4">
      {groups.map((group) => (
        <section key={group.title} className="flex flex-col gap-space-2">
          <h4 className="text-caption font-semibold uppercase tracking-wide text-gold-shadow">{group.title}</h4>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-space-2 text-small">
            {group.rows.map(([label, value]) => (
              <div key={label}>
                <dt className="text-gray-mid">{label}</dt>
                <dd className="whitespace-pre-wrap">{value || empty}</dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
      <section className="flex flex-col gap-space-2">
        <h4 className="text-caption font-semibold uppercase tracking-wide text-gold-shadow">Business Brokerage License Type</h4>
        {broker.businessBrokerageLicenseTypes.length > 0 ? (
          <div className="flex flex-wrap gap-space-2">
            {broker.businessBrokerageLicenseTypes.map((licenseType) => (
              <span key={licenseType} className="rounded-pill border border-gold/40 bg-gold/10 px-space-2 py-[2px] text-caption text-onyx">
                {licenseType}
              </span>
            ))}
          </div>
        ) : <p className="text-small text-gray-mid">{empty}</p>}
      </section>
      <section className="flex flex-col gap-space-2">
        <h4 className="text-caption font-semibold uppercase tracking-wide text-gold-shadow">Licensing Details</h4>
        {broker.licensingDetails.length > 0 ? (
          <div className="overflow-x-auto rounded border border-border-light">
            <table className="min-w-full text-small">
              <thead className="bg-paper-warm text-left text-caption uppercase tracking-wide text-gray-mid">
                <tr>
                  <th className="px-space-2 py-space-2">License Type & Issuing Organization</th>
                  <th className="px-space-2 py-space-2">License Number</th>
                  <th className="px-space-2 py-space-2">Expiration Date</th>
                  <th className="px-space-2 py-space-2">State</th>
                </tr>
              </thead>
              <tbody>
                {broker.licensingDetails.map((row, index) => (
                  <tr key={index} className="border-t border-border-light">
                    <td className="px-space-2 py-space-2">{row.licenseTypeIssuingOrganization || empty}</td>
                    <td className="px-space-2 py-space-2">{row.licenseNumber || empty}</td>
                    <td className="px-space-2 py-space-2">{row.expirationDate || empty}</td>
                    <td className="px-space-2 py-space-2">{row.state || empty}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="text-small text-gray-mid">{empty}</p>}
      </section>
    </div>
  );
}

// The broker's people are normalized BrokerContact rows (the platform pattern;
// name/phone/email/title live here, not on the broker). This card is the single
// management surface for them: add / edit / remove a contact, designate the
// primary, and activate / deactivate. The broker's PrimaryContactId FK points at
// the primary contact, which the list / search / header summaries resolve.
function BrokerContactsCard({
  broker,
  canUpdate,
}: {
  broker: BrokerDetailDto;
  canUpdate: boolean;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<{ contact: BrokerContactDto | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const dialogs = useDialogs();

  // Primary first, then active, then by name — a stable, readable order.
  const contacts = [...broker.contacts].sort((a, b) =>
    (a.isPrimary === b.isPrimary ? 0 : a.isPrimary ? -1 : 1) ||
    (a.isActive === b.isActive ? 0 : a.isActive ? -1 : 1) ||
    `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));

  // Contacts feed the brokers list's Contact column — cover it too.
  const invalidate = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: brokerKeys.detail(broker.id) }),
      qc.invalidateQueries({ queryKey: brokerKeys.lists() }),
    ]);

  const setPrimary = useMutation({
    mutationFn: (c: BrokerContactDto) =>
      brokersApi.upsertContact(broker.id, c.id, {
        firstName: c.firstName,
        lastName: c.lastName,
        title: c.title ?? null,
        email: c.email ?? null,
        phone: c.phone ?? null,
        isPrimary: true,
        isActive: true,
      }),
    onSuccess: () => void invalidate(),
    onError: (e: Error) => setError(e.message),
  });

  // Active is managed from the list (the add/edit form no longer carries the
  // toggle). Deactivating the primary contact hands primary to the next active
  // contact server-side.
  const setActive = useMutation({
    mutationFn: ({ c, active }: { c: BrokerContactDto; active: boolean }) =>
      brokersApi.upsertContact(broker.id, c.id, {
        firstName: c.firstName,
        lastName: c.lastName,
        title: c.title ?? null,
        email: c.email ?? null,
        phone: c.phone ?? null,
        isPrimary: c.isPrimary,
        isActive: active,
      }),
    onSuccess: () => void invalidate(),
    onError: (e: Error) => setError(e.message),
  });

  const remove = async (c: BrokerContactDto) => {
    const ok = await dialogs.confirm({
      title: "Remove contact",
      message: `Remove ${`${c.firstName} ${c.lastName}`.trim() || "this contact"}?`,
      confirmLabel: "Remove",
      destructive: true,
    });
    if (!ok) return;
    try {
      await brokersApi.deleteContact(broker.id, c.id);
      void invalidate();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Remove failed.");
    }
  };

  return (
    <div className="bg-white-pure border border-border-light rounded-lg p-space-4 md:col-span-3">
      <div className="flex items-center justify-between mb-space-2">
        <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow">
          Contacts
        </h3>
        {canUpdate && (
          <Button variant="secondary" onClick={() => setEditing({ contact: null })}>
            <Plus size={14} aria-hidden /> Add contact
          </Button>
        )}
      </div>
      {contacts.length === 0 ? (
        <div className="text-caption text-gray-mid italic">No contacts.</div>
      ) : (
        <ul className="divide-y divide-border-light">
          {contacts.map((c) => (
            <li key={c.id} className="flex items-start justify-between gap-space-3 py-space-3 first:pt-0">
              <div className="text-small">
                <div className="flex items-center gap-space-2">
                  <span className="font-semibold text-onyx">
                    {`${c.firstName} ${c.lastName}`.trim() || "—"}
                  </span>
                  {c.isPrimary && <Badge status="gold">Primary</Badge>}
                  {!c.isActive && <Badge status="archived">Inactive</Badge>}
                </div>
                <dl className="mt-space-1 grid grid-cols-[auto,1fr] gap-x-space-3 gap-y-space-1 text-caption text-gray-mid">
                  {c.title && (<><dt>Title</dt><dd className="text-onyx">{c.title}</dd></>)}
                  <dt>Email</dt><dd className="text-onyx">{c.email ?? "—"}</dd>
                  <dt>Phone</dt><dd className="text-onyx">{c.phone ?? "—"}</dd>
                </dl>
              </div>
              {canUpdate && (
                <div className="flex shrink-0 flex-col items-end gap-space-1">
                  <Button variant="text" size="sm" onClick={() => setEditing({ contact: c })}>
                    <Pencil size={14} aria-hidden /> Edit
                  </Button>
                  {!c.isPrimary && c.isActive && (
                    <Button
                      variant="text"
                      size="sm"
                      onClick={() => setPrimary.mutate(c)}
                      disabled={setPrimary.isPending}
                    >
                      <Star size={14} aria-hidden /> Set primary
                    </Button>
                  )}
                  {/* The primary contact is always active; to deactivate it,
                      first set another active contact as primary. This keeps a
                      broker from silently losing its name/email/phone. */}
                  {!c.isPrimary && (
                    <Button
                      variant="text"
                      size="sm"
                      onClick={() => setActive.mutate({ c, active: !c.isActive })}
                      disabled={setActive.isPending}
                    >
                      {c.isActive ? "Deactivate" : "Activate"}
                    </Button>
                  )}
                  <Button variant="text" size="sm" onClick={() => remove(c)}>
                    <Trash2 size={14} aria-hidden /> Remove
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {error && (
        <p className="text-caption text-status-error mt-space-2" role="alert">{error}</p>
      )}
      {editing && (
        <ContactEditModal
          brokerId={broker.id}
          contact={editing.contact}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function ContactEditModal({
  brokerId,
  contact,
  onClose,
}: {
  brokerId: string;
  contact: BrokerContactDto | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    firstName: contact?.firstName ?? "",
    lastName: contact?.lastName ?? "",
    title: contact?.title ?? "",
    email: contact?.email ?? "",
    phone: contact?.phone ?? "",
  });
  const [error, setError] = useState<string | null>(null);

  // Email and phone are optional, but if typed they must be valid — mirrors
  // the Leads form (LeadFormModal) which reuses the same field validators.
  const emailError = emailFieldError(form.email, { required: false });
  const phoneError = phoneFieldError(form.phone, { required: false });

  const mutation = useMutation({
    mutationFn: () =>
      brokersApi.upsertContact(brokerId, contact?.id, {
        firstName: form.firstName,
        lastName: form.lastName,
        title: form.title || null,
        email: form.email || null,
        phone: form.phone || null,
        // Primary is designated from the contacts list ("Set primary"), and new
        // contacts are active. The server auto-assigns primary to the first /
        // only active contact, so a fresh add sends isPrimary=false here.
        isPrimary: contact?.isPrimary ?? false,
        isActive: contact?.isActive ?? true,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: brokerKeys.detail(brokerId) });
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={contact ? "Edit contact" : "Add contact"}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            loading={mutation.isPending}
            disabled={!form.firstName || !!emailError || !!phoneError}
            onClick={() => {
              setError(null);
              if (emailError || phoneError) {
                setError(emailError ?? phoneError ?? null);
                return;
              }
              mutation.mutate();
            }}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-space-3">
        <FormField label="First name" required>
          <Input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
        </FormField>
        <FormField label="Last name">
          <Input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
        </FormField>
        <FormField label="Title">
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </FormField>
        <FormField label="Email" error={emailError}>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            invalid={!!emailError}
          />
        </FormField>
        <FormField label="Phone" error={phoneError}>
          <Input
            type="tel"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            invalid={!!phoneError}
          />
        </FormField>
      </div>
      {error && (
        <p className="text-caption text-status-error mt-space-2" role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
}

